import { supabase } from '../supabase/client';
import type { TicketVorlage } from '../ki/ticketVorlage';
import { titelMitObjekt, type TitelObjekt } from './ticketTitel';

export type TicketInsert = {
  titel: string; beschreibung: string | null; status: 'neu'; prioritaet: string; faellig_am: string | null;
  kategorie_id: string | null; objekt_id: string | null; mieter_id: string | null; erstellt_von: string | null;
  einheit_id: string | null; quelle_email_id: string | null; absender_typ: string | null;
  unterkategorie_id: string | null; erkannte_aufgabe: string | null; naechster_schritt: string | null;
  bearbeiter_vorschlag_id: string | null; bearbeiter_vorschlag_typ: string | null;
  dienstleister_vorschlag_id: string | null; ki_confidence: number | null; ist_rechtlich_sensibel: boolean;
  // #489 Vorgang – nur im Insert, wenn gesetzt
  vorgangsart_id?: string; vorgang_angaben?: Record<string, unknown> | null; wiedervorlage_am?: string;
  zustaendig_id?: string; zustaendig_typ?: 'mitarbeiter' | 'team';
};

/**
 * `objekt` (optional) stellt Objektnummer und Straße vor den Titel — bei aus E-Mails
 * erzeugten Tickets sieht man sonst in Liste, Mail und Export nicht, um welches Haus es
 * geht (User-Wunsch 2026-08-12). Ohne Objekt bleibt der Titel unverändert.
 */
export function baueTicketInsert(
  vorlage: TicketVorlage, erstelltVon: string | null, objekt?: TitelObjekt | null,
): TicketInsert {
  const vorgang: Partial<TicketInsert> = {};
  if (vorlage.vorgangsart_id) {
    vorgang.vorgangsart_id = vorlage.vorgangsart_id;
    vorgang.vorgang_angaben = vorlage.vorgang_angaben ?? null;
    if (vorlage.zustaendig_id && vorlage.zustaendig_typ) { vorgang.zustaendig_id = vorlage.zustaendig_id; vorgang.zustaendig_typ = vorlage.zustaendig_typ; }
  }
  // Wiedervorlage auch ohne Vorgang (#586 Etappe 4: Wiedervorlage aus dem Schreiben-Assistenten)
  if (vorlage.wiedervorlage_am) vorgang.wiedervorlage_am = vorlage.wiedervorlage_am;
  return {
    ...vorgang,
    titel: titelMitObjekt(vorlage.titel, objekt),
    beschreibung: vorlage.beschreibung.trim() || null,
    status: 'neu',
    prioritaet: vorlage.prioritaet,
    faellig_am: vorlage.faellig_am ?? null,
    kategorie_id: vorlage.kategorie_id || null,
    objekt_id: vorlage.objekt_id || null,
    mieter_id: vorlage.mieter_id || null,
    erstellt_von: erstelltVon || null,
    einheit_id: vorlage.einheit_id ?? null,
    quelle_email_id: vorlage.quelle_email_id ?? null,
    absender_typ: vorlage.absender_typ ?? null,
    unterkategorie_id: vorlage.unterkategorie_id ?? null,
    erkannte_aufgabe: vorlage.erkannte_aufgabe ?? null,
    naechster_schritt: vorlage.naechster_schritt ?? null,
    bearbeiter_vorschlag_id: vorlage.bearbeiter_vorschlag_id ?? null,
    bearbeiter_vorschlag_typ: vorlage.bearbeiter_vorschlag_typ ?? null,
    dienstleister_vorschlag_id: vorlage.dienstleister_vorschlag_id ?? null,
    ki_confidence: vorlage.ki_confidence ?? null,
    ist_rechtlich_sensibel: vorlage.ist_rechtlich_sensibel ?? false,
  };
}

export async function erstelleTicket(
  vorlage: TicketVorlage, erstelltVon: string | null,
): Promise<{ id: string; ticket_nummer: string }> {
  // #580: Den Objektbezug im Titel setzt seit 29.10.2026 der DB-Trigger tickets_titel_objekt
  // (für alle Anlagewege, abschaltbar im Control Center) – hier nicht mehr doppelt.
  const { data: ticket, error } = await supabase
    .from('tickets').insert(baueTicketInsert(vorlage, erstelltVon))
    .select('id, ticket_nummer').single();
  if (error) throw error;

  const links: any[] = [];
  if (vorlage.objekt_id) links.push({ source_type: 'ticket', source_id: ticket.id, target_type: 'objekt', target_id: vorlage.objekt_id, link_type: 'zuordnung' });
  if (vorlage.mieter_id) links.push({ source_type: 'ticket', source_id: ticket.id, target_type: 'mieter', target_id: vorlage.mieter_id, link_type: 'zuordnung' });
  if (vorlage.einheit_id) links.push({ source_type: 'ticket', source_id: ticket.id, target_type: 'einheit', target_id: vorlage.einheit_id, link_type: 'zuordnung' });
  if (links.length) await supabase.from('entity_links').insert(links);

  await supabase.from('ticket_historie').insert({
    ticket_id: ticket.id, benutzer_id: erstelltVon, aktion: 'erstellt',
    neuer_wert: `Ticket ${ticket.ticket_nummer} erstellt`,
  });

  return ticket as { id: string; ticket_nummer: string };
}
